import path from "path";
import { Binary } from "./Binary";
import { Config as ConfigClass } from './Config';
import { ProcessResponse, ResponseEngine } from "../@types/Engine";
import { BinaryType } from "../@types/Binary";
import { createRequire } from 'module';
import * as net from 'net';
import { spawn, ChildProcess } from "child_process";

// Almacén global de servidores TCP y conexiones persistentes
const globalTcpServers = new Map<string, { port: number, process: ChildProcess | null }>();
const globalTcpConnections = new Map<string, net.Socket | null>();

// Query Cache para evitar JSON.parse repetido
const queryCache = new Map<string, any>();
let cacheSize = 0;
const MAX_CACHE_SIZE = 500;

class QueryEngine {
    private name: string;
    private config: any;
    private arguments: any;
    private binary: BinaryType | null = null;
    private timeout: number;
    private connectionId: string;
    private tcpPort: number;

    constructor(name: string, timeout = 30000) {
        this.name = name;
        this.config = this.setConfig(name);
        this.arguments = this.setArguments();
        this.timeout = timeout;
        this.connectionId = `${name}_${this.config.type}_${this.config.config.DATABASE}_${this.config.config.HOST || 'localhost'}`;
        this.tcpPort = this.generatePort();
    }

    private generatePort(): number {
        // Empezar desde el puerto 9944 y buscar hacia abajo
        return 9944;
    }

    private async findAvailablePort(startPort: number): Promise<number> {
        for (let port = startPort; port >= 9900; port--) {
            if (await this.isPortAvailable(port)) {
                return port;
            }
        }
        throw new Error('No available ports found in range 9900-9944');
    }

    private isPortAvailable(port: number): Promise<boolean> {
        return new Promise((resolve) => {
            const tester = net.createServer();

            tester.once('error', () => resolve(false));
            tester.once('listening', () => {
                tester.close();
                resolve(true);
            });

            tester.listen(port, '127.0.0.1');
        });
    }

    async initializeBinary(): Promise<void> {
        if (!this.binary) {
            this.binary = await Binary.get();
        }
    }

    setArguments() {
        let args = [];
        if (this.config.type == 'sqlite') {
            args = [
                '--id', 'dbcube-' + this.name,
                '--database-ref', this.name,
                '--database', this.config.config.DATABASE + ".db",
                '--motor', this.config.type
            ];
        } else {
            args = [
                '--id', 'dbcube-' + this.name,
                '--database-ref', this.name,
                '--database', this.config.config.DATABASE,
                '--host', this.config.config.HOST,
                '--port', this.config.config.PORT,
                '--user', this.config.config.USER,
                '--password', this.config.config.PASSWORD,
                '--motor', this.config.type
            ];
        }
        return args;
    }

    setConfig(name: string) {
        const configInstance = new ConfigClass();

        try {
            const configFilePath = path.resolve(process.cwd(), 'dbcube.config.js');
            const requireUrl = typeof __filename !== 'undefined' ? __filename : process.cwd();
            const require = createRequire(requireUrl);
            // Clear require cache to ensure fresh load (safe check for bundled environments)
            if (require.cache && require.resolve) {
                try {
                    delete require.cache[require.resolve(configFilePath)];
                } catch (e) {
                    // Ignore errors in bundled environments
                }
            }
            const configModule = require(configFilePath);
            const configFn = configModule.default || configModule;

            if (typeof configFn === 'function') {
                configFn(configInstance);
            } else {
                console.error('❌ El archivo dbcube.config.js no exporta una función.');
            }
        } catch (error: any) {
            console.error('❌ Error loading config file:', error.message);
            if (error.code === 'MODULE_NOT_FOUND') {
                console.error('❌ Config file not found, please create a dbcube.config.js file');
            }
        }

        return configInstance.getDatabase(name);
    }

    getConfig() {
        return this.config;
    }

    async run(binary: string, args: string[]): Promise<ResponseEngine> {
        // Detectar si es una acción execute
        const actionIndex = args.findIndex(arg => arg === '--action');
        const isExecuteAction = actionIndex !== -1 && args[actionIndex + 1] === 'execute';

        if (isExecuteAction) {
            // Para consultas execute, usar servidor TCP
            return this.executeWithTcpServer(args);
        } else {
            // Para otras acciones (connect, disconnect, list), usar método normal
            return this.createProcess(binary, args);
        }
    }

    private async executeWithTcpServer(args: string[]): Promise<ResponseEngine> {
        const serverInfo = globalTcpServers.get(this.connectionId);

        // Si no hay servidor TCP, iniciarlo
        if (!serverInfo || !serverInfo.process || serverInfo.process.killed) {
            await this.startTcpServer();
            // Solo verificar la primera vez
            await this.waitForServerReady();
        }

        // Enviar consulta via TCP (sin verificaciones adicionales para máxima velocidad)
        return this.sendTcpRequestFast(args);
    }

    private async waitForServerReady(): Promise<void> {
        const maxRetries = 10;
        const retryDelay = 500; // 500ms entre intentos

        for (let i = 0; i < maxRetries; i++) {
            if (await this.isServerResponding()) {
                return;
            }
            await this.sleep(retryDelay);
        }
        throw new Error('TCP server failed to become ready within timeout');
    }

    private async isServerResponding(): Promise<boolean> {
        return new Promise((resolve) => {
            const client = new net.Socket();
            const timeout = setTimeout(() => {
                client.destroy();
                resolve(false);
            }, 1000);

            client.connect(this.tcpPort, '127.0.0.1', () => {
                clearTimeout(timeout);
                client.destroy();
                resolve(true);
            });

            client.on('error', () => {
                clearTimeout(timeout);
                resolve(false);
            });
        });
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private getCachedDML(dmlJson: string): any {
        // Buscar en cache primero
        if (queryCache.has(dmlJson)) {
            return queryCache.get(dmlJson);
        }

        // Parse y guardar en cache si hay espacio
        const parsed = JSON.parse(dmlJson);

        if (cacheSize < MAX_CACHE_SIZE) {
            queryCache.set(dmlJson, parsed);
            cacheSize++;
        }

        return parsed;
    }

    private async startTcpServer(): Promise<void> {
        await this.initializeBinary();

        if (!this.binary) {
            throw new Error('Binary not initialized');
        }

        // Buscar puerto disponible
        this.tcpPort = await this.findAvailablePort(this.tcpPort);

        return new Promise((resolve, reject) => {
            // Iniciar el query-engine como servidor TCP
            // Usar --tcp-port para el servidor TCP (diferente de --port que es para MySQL/PostgreSQL)
            const serverArgs = [...this.arguments, '--action', 'server', '--tcp-port', this.tcpPort.toString()];
            const serverProcess = spawn(this.binary!['query_engine' as keyof BinaryType], serverArgs);

            let started = false;

            const timeout = setTimeout(() => {
                if (!started) {
                    serverProcess.kill();
                    reject(new Error('TCP server startup timeout'));
                }
            }, 15000); // Aumentar timeout a 15 segundos

            serverProcess.stdout.on('data', (data) => {
                const output = data.toString();

                if (output.includes('TCP Server listening on')) {
                    if (!started) {
                        started = true;
                        clearTimeout(timeout);
                        globalTcpServers.set(this.connectionId, {
                            port: this.tcpPort,
                            process: serverProcess
                        });
                        resolve();
                    }
                }
            });

            serverProcess.stderr.on('data', (data) => {
                console.error(`[TCP Server Error] ${data.toString().trim()}`);
            });

            serverProcess.on('close', (code) => {
                globalTcpServers.delete(this.connectionId);
            });

            serverProcess.on('error', (error) => {
                if (!started) {
                    clearTimeout(timeout);
                    reject(error);
                }
            });
        });
    }

    private async sendTcpRequest(args: string[]): Promise<ResponseEngine> {
        return new Promise((resolve, reject) => {
            const client = new net.Socket();
            let responseBuffer = '';
            let isResolved = false;

            const timeout = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    client.destroy();
                    reject(new Error('TCP request timeout'));
                }
            }, this.timeout * 2); // Doble timeout para TCP requests

            client.connect(this.tcpPort, '127.0.0.1', () => {
                // Extraer el DML de los argumentos
                const dmlIndex = args.findIndex(arg => arg === '--dml');
                const dmlJson = dmlIndex !== -1 ? args[dmlIndex + 1] : null;

                if (!dmlJson) {
                    if (!isResolved) {
                        isResolved = true;
                        clearTimeout(timeout);
                        client.destroy();
                        reject(new Error('No DML found in arguments'));
                    }
                    return;
                }

                // Enviar comando con el formato correcto que espera el servidor TCP
                const command = {
                    action: 'execute',
                    dml: dmlJson
                };
                client.write(JSON.stringify(command));
            });

            client.on('data', (data) => {
                responseBuffer += data.toString();

                // Buscar respuesta con formato PROCESS_RESPONSE: igual que en Engine
                const match = responseBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match && !isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    client.destroy();

                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolve({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        reject(new Error('Failed to parse TCP response'));
                    }
                }
            });

            client.on('error', (error) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    reject(error);
                }
            });

            client.on('close', () => {
                if (!isResolved) {
                    clearTimeout(timeout);
                    // Solo rechazar si no hay una respuesta válida
                    if (!responseBuffer.includes('PROCESS_RESPONSE:')) {
                        reject(new Error('Connection closed without valid response'));
                    }
                }
            });
        });
    }

    private async sendTcpRequestFast(args: string[]): Promise<ResponseEngine> {
        // Intentar usar conexión persistente primero
        const existingConnection = globalTcpConnections.get(this.connectionId);

        if (existingConnection && existingConnection.readyState === 'open') {
            try {
                return await this.sendOnExistingConnection(existingConnection, args);
            } catch (error) {
                // Si falla, remover y crear nueva conexión
                globalTcpConnections.delete(this.connectionId);
                existingConnection.destroy();
            }
        }

        // Crear nueva conexión persistente
        return await this.createPersistentConnection(args);
    }

    private async sendOnExistingConnection(connection: net.Socket, args: string[]): Promise<ResponseEngine> {
        return new Promise((resolve, reject) => {
            const dmlIndex = args.findIndex(arg => arg === '--dml');
            const dmlJson = dmlIndex !== -1 ? args[dmlIndex + 1] : null;

            if (!dmlJson) {
                reject(new Error('No DML found in arguments'));
                return;
            }

            let responseBuffer = '';
            let isResolved = false;

            const timeout = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    reject(new Error('TCP request timeout'));
                }
            }, 100); // Timeout ultra-corto para detectar conexiones muertas

            const onData = (data: Buffer) => {
                responseBuffer += data.toString();
                const match = responseBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match && !isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    connection.off('data', onData);
                    connection.off('error', onError);

                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolve({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        reject(new Error('Failed to parse TCP response'));
                    }
                }
            };

            const onError = (error: Error) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    connection.off('data', onData);
                    connection.off('error', onError);
                    reject(error);
                }
            };

            connection.on('data', onData);
            connection.on('error', onError);

            // Enviar comando
            const command = {
                action: 'execute',
                dml: dmlJson
            };
            connection.write(JSON.stringify(command));
        });
    }

    private async createPersistentConnection(args: string[]): Promise<ResponseEngine> {
        return new Promise((resolve, reject) => {
            // Intentar reutilizar conexión existente
            let client = globalTcpConnections.get(this.connectionId);
            let isNewConnection = false;

            if (!client || client.destroyed) {
                // Crear nueva conexión si no existe o está destruida
                client = new net.Socket();
                isNewConnection = true;

                // Configurar socket para máxima velocidad y estabilidad
                client.setNoDelay(true); // Disable Nagle's algorithm
                client.setKeepAlive(true, 60000); // Keep connection alive
                client.setMaxListeners(20); // Aumentar listeners para múltiples requests
            }

            let responseBuffer = '';
            let isResolved = false;

            const timeout = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    reject(new Error('TCP connection timeout'));
                }
            }, 5000);

            const executeQuery = () => {
                const dmlIndex = args.findIndex(arg => arg === '--dml');
                const dmlJson = dmlIndex !== -1 ? args[dmlIndex + 1] : null;

                if (!dmlJson) {
                    clearTimeout(timeout);
                    if (!isResolved) {
                        isResolved = true;
                        reject(new Error('No DML found in arguments'));
                    }
                    return;
                }

                // Enviar comando
                const command = {
                    action: 'execute',
                    dml: dmlJson
                };

                const commandStr = JSON.stringify(command);

                // Manejo de backpressure: verificar si el buffer está lleno
                const canWrite = client!.write(commandStr);
                if (!canWrite) {
                    // Buffer lleno, esperar el evento 'drain'
                    client!.once('drain', () => {
                        // Buffer vaciado, listo para más datos
                    });
                }
            };

            // Handler para datos recibidos (temporal para esta request)
            const dataHandler = (data: Buffer) => {
                responseBuffer += data.toString();
                const match = responseBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match && !isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);

                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);

                        // Limpiar buffer para próxima request
                        responseBuffer = '';

                        // Remover listener temporal de data
                        client!.removeListener('data', dataHandler);

                        resolve({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        client!.removeListener('data', dataHandler);
                        reject(new Error('Failed to parse TCP response'));
                    }
                }
            };

            // Handler para errores (permanente si es nueva conexión)
            const errorHandler = (error: Error) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    globalTcpConnections.delete(this.connectionId);
                    client!.removeListener('data', dataHandler);
                    reject(error);
                }
            };

            // Handler para cierre de conexión (permanente si es nueva conexión)
            const closeHandler = () => {
                globalTcpConnections.delete(this.connectionId);
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeout);
                    client!.removeListener('data', dataHandler);
                    reject(new Error('Connection closed unexpectedly'));
                }
            };

            if (isNewConnection) {
                // Nueva conexión: conectar primero
                client!.connect(this.tcpPort, '127.0.0.1', () => {
                    clearTimeout(timeout);

                    // Guardar conexión para reutilización
                    globalTcpConnections.set(this.connectionId, client!);

                    // Agregar handlers permanentes
                    client!.on('error', errorHandler);
                    client!.on('close', closeHandler);

                    // Agregar handler temporal de data para esta request
                    client!.on('data', dataHandler);

                    executeQuery();
                });
            } else {
                // Conexión existente: usar directamente
                clearTimeout(timeout);

                // Agregar handler temporal de data para esta request
                client!.on('data', dataHandler);
                executeQuery();
            }
        });
    }

    private async createProcess(binary: string, args: string[]): Promise<ResponseEngine> {
        await this.initializeBinary();

        if (!this.binary) {
            throw new Error('Binary not initialized');
        }

        return new Promise<ResponseEngine>((resolve, reject) => {
            const child = spawn(this.binary![binary as keyof BinaryType], [...this.arguments, ...args]);

            let stdoutBuffer = '';
            let stderrBuffer = '';
            let isResolved = false;

            const timeoutId = setTimeout(() => {
                if (!isResolved) {
                    isResolved = true;
                    child.kill();
                    reject(new Error('Process timeout'));
                }
            }, this.timeout);

            const resolveOnce = (response: ResponseEngine) => {
                if (!isResolved) {
                    isResolved = true;
                    clearTimeout(timeoutId);
                    resolve(response);
                }
            };

            child.stdout.on('data', (data) => {
                stdoutBuffer += data.toString();

                const match = stdoutBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match) {
                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolveOnce({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        resolveOnce({
                            status: 500,
                            message: 'Failed to parse response JSON',
                            data: null
                        });
                    }
                }
            });

            child.stderr.on('data', (data) => {
                stderrBuffer += data.toString();

                const match = stderrBuffer.match(/PROCESS_RESPONSE:(\{.*\})/);
                if (match) {
                    try {
                        const response: ProcessResponse = JSON.parse(match[1]);
                        resolveOnce({
                            status: response.status,
                            message: response.message,
                            data: response.data
                        });
                    } catch (error) {
                        resolveOnce({
                            status: 500,
                            message: 'Failed to parse response JSON',
                            data: null
                        });
                    }
                }
            });

            child.on('close', (code) => {
                clearTimeout(timeoutId);
                if (!isResolved) {
                    resolveOnce({
                        status: code === 0 ? 200 : 500,
                        message: code === 0 ? 'Process completed' : `Process exited with code ${code}`,
                        data: null
                    });
                }
            });

            child.on('error', (error) => {
                clearTimeout(timeoutId);
                if (!isResolved) {
                    resolveOnce({
                        status: 500,
                        message: `Process error: ${error.message}`,
                        data: null
                    });
                }
            });

            child.unref();
        });
    }

    async disconnect(): Promise<ResponseEngine> {
        // Cerrar conexión TCP persistente si existe
        const connection = globalTcpConnections.get(this.connectionId);
        if (connection && connection.readyState === 'open') {
            connection.write(JSON.stringify({ action: 'disconnect' }));
            connection.destroy();
            globalTcpConnections.delete(this.connectionId);
        }

        const serverInfo = globalTcpServers.get(this.connectionId);

        if (serverInfo && serverInfo.process && !serverInfo.process.killed) {
            serverInfo.process.kill();
            globalTcpServers.delete(this.connectionId);

            return {
                status: 200,
                message: 'TCP server and connections stopped',
                data: null
            };
        }

        return {
            status: 200,
            message: 'No TCP server to stop',
            data: null
        };
    }
}

export { QueryEngine }